import React, { useState, useEffect } from "react";
import { Layout, Responsive, WidthProvider } from "react-grid-layout";
import { LayoutComponent } from "../../../interfaces/LayoutComponent";
import { classNames } from "../../../utils/CssUtil";
import DynamicComponent from "../../layoutDesign/DynamicComponent";
import { Visit } from "../../../interfaces/Visit";
import { getNewValue } from "../../../utils/LayoutUtil";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "../CustomPage.css";
import Modal from "../../../components/Modal";
import AddMedicine from "../../medicines/AddMedicine";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface IPDCustomFormProps {
  gridItems: LayoutComponent[];
  layout: Layout[];
  visit: Visit;
  initialData?: any;
  onDataChange: (data: any) => void;
}

const IPDCustomForm: React.FC<IPDCustomFormProps> = ({
  gridItems,
  layout,
  visit,
  initialData = {},
  onDataChange,
}) => {
  // Initialize formData with predefined values from layout design if initialData is empty
  const initializeFormData = () => {
    if (Object.keys(initialData).length > 0) {
      // If initialData exists (editing existing entry), use it
      return initialData;
    }
    // If initialData is empty (new entry), extract predefined values from layout design
    const predefinedData: any = {};
    gridItems.forEach((gridItem: any) => {
      const key = gridItem.data?.key || `field_${gridItem.id}`;
      if (gridItem.data?.value !== undefined && gridItem.data?.value !== null && gridItem.data?.value !== '') {
        predefinedData[key] = gridItem.data.value;
      }
    });
    return predefinedData;
  };

  const [formData, setFormData] = useState<any>(initializeFormData());
  const [isMedicineModalOpen, setIsMedicineModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // Only update if initialData has values (editing existing entry)
      setFormData(initialData);
    } else {
      // If initialData is empty, re-initialize with predefined values
      const predefinedData: any = {};
      gridItems.forEach((gridItem: any) => {
        const key = gridItem.data?.key || `field_${gridItem.id}`;
        if (gridItem.data?.value !== undefined && gridItem.data?.value !== null && gridItem.data?.value !== '') {
          predefinedData[key] = gridItem.data.value;
        }
      });
      setFormData(predefinedData);
    }
  }, [initialData, gridItems]);

  const handleValueChange = (e: any, component: LayoutComponent, index: number) => {
    const key = component.data?.key || `field_${component.id}`;
    
    // Use getNewValue utility to extract value properly based on component type
    const value = getNewValue(component.component, e);
    
    const newData = {
      ...formData,
      [key]: value !== null ? value : (e.target ? e.target.value : e),
    };
    
    setFormData(newData);
    onDataChange(newData);
  };

  const handleEvent = (event: string, payload: any) => {
    if (event === "create-medicine") {
      setSearchTerm(payload);
      setIsMedicineModalOpen(true);
    }
  };

  if (!gridItems || gridItems.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <p>No form fields configured. Please design a layout first.</p>
      </div>
    );
  }

  // Ensure layout items have string keys (matching CustomPage approach)
  const layoutWithStringKeys = layout.map((item) => ({
    ...item,
    i: String(item.i),
  }));

  // Update gridItems with current form data values (matching AddVisit.tsx approach)
  // Priority: 1. Saved entry data (formData), 2. Predefined layout value (gridItem.data.value)
  const gridItemsWithValues = gridItems.map((gridItem: any) => {
    const updatedItem = { ...gridItem };
    const key = gridItem.data?.key || `field_${gridItem.id}`;
    
    // Priority: formData (saved entry) > predefined value (layout design) > null
    // This ensures predefined values from layout design are preserved for new entries
    const value = formData[key] !== undefined && formData[key] !== null && formData[key] !== ''
      ? formData[key] 
      : (gridItem.data?.value ?? null);
    
    updatedItem.data = {
      ...gridItem.data,
      value: value,
    };
    
    return updatedItem;
  });

  // Create visit object with form data merged into meta.layoutContent
  const visitWithData = {
    ...visit,
    meta: {
      ...visit.meta,
      layoutContent: {
        ...visit.meta?.layoutContent,
        ...formData,
      },
    },
  };

  return (
    <div className="custom-page h-[calc(100vh-273px)]">
      <ResponsiveGridLayout
        autoSize={true}
        className="layout"
        layouts={{ lg: layoutWithStringKeys }}
        rowHeight={40}
        cols={{ lg: 6, md: 6, sm: 6, xs: 4, xxs: 2 }}
        isDraggable={false}
        isResizable={false}
        onLayoutChange={() => {}}
      >
        {gridItemsWithValues.map((gridItem: any, index) => {
          return (
            <div
              className={classNames(
                gridItem.component === "FasciaChart"
                  ? "border-0 bg-transperant"
                  : "bg-white border-solid box-border",
                "p-2"
              )}
              key={gridItem.id}
            >
              <DynamicComponent
                key={gridItem.id}
                gridItem={gridItem}
                setValue={(e: any) => handleValueChange(e, gridItem, index)}
                visit={visitWithData}
                onEvent={handleEvent}
              />
            </div>
          );
        })}
      </ResponsiveGridLayout>
      {isMedicineModalOpen && (
        <Modal
          modalContent={
            <AddMedicine
              text={searchTerm}
              editableId={null}
              closeModal={() => setIsMedicineModalOpen(false)}
            />
          }
        />
      )}
    </div>
  );
};

export default IPDCustomForm;

